import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Text,
  Group,
  Button,
  Badge,
  Card,
  SimpleGrid,
  Alert,
  ThemeIcon,
  Table,
  Tabs,
  Loader,
  NumberInput,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconFileInvoice,
  IconCalendar,
  IconCheck,
  IconAlertTriangle,
  IconClock,
  IconSend,
  IconRefresh,
  IconUsers,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { PageHeader } from '../../components/PageHeader';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';

/**
 * TaxFilingCenterPage — 1099-NEC generation and filing.
 *
 * Backend generates, frontend reviews:
 *   1. contractorsFor1099 query → discover eligible contractors
 *   2. generate1099s mutation → generate PDFs via forms engine
 *   3. Review generated forms → approve/reject
 *
 * Workflow: wf-2-0-017 (batch generation), wf-2-0-018 (corrections)
 * Rules: rl-2-0-009 (correction rules)
 * Integration: int-erp-tax-001 (Avalara/Vertex)
 */

// ============================================================================
// GRAPHQL OPERATIONS
// ============================================================================

const GET_CONTRACTORS_FOR_1099 = gql`
  query GetContractorsFor1099($tenantId: String!, $taxYear: Int!) {
    contractorsFor1099(tenantId: $tenantId, taxYear: $taxYear) {
      contractorId
      contractorName
      taxYear
      totalCompensation
      federalTaxWithheld
      stateTaxWithheld
      paymentCount
      requires1099
    }
  }
`;

const GENERATE_1099S = gql`
  mutation Generate1099S($input: Generate1099SInput!) {
    generate1099S(input: $input) {
      batch {
        id
        tenantId
        formType
        taxYear
        status
        totalForms
        formsGenerated
        formsWithErrors
        documentJobId
        errors {
          entityId
          entityName
          message
          code
        }
        createdAt
        createdBy
      }
      message
    }
  }
`;

// ============================================================================
// TYPES (from backend schema — matches ContractorYtdSummaryGql)
// ============================================================================

interface ContractorYtdSummary {
  contractorId: string;
  contractorName: string;
  taxYear: number;
  totalCompensation: string;
  federalTaxWithheld: string;
  stateTaxWithheld: string;
  paymentCount: number;
  requires1099: boolean;
}

interface TaxFormBatch {
  id: string;
  tenantId: string;
  formType: string;
  taxYear: number;
  status: string;
  totalForms: number;
  formsGenerated: number;
  formsWithErrors: number;
  documentJobId: string | null;
  errors: Array<{
    entityId: string;
    entityName: string;
    message: string;
    code: string;
  }>;
  createdAt: string;
  createdBy: string;
}

interface ContractorsFor1099Data {
  contractorsFor1099: ContractorYtdSummary[];
}

interface Generate1099SData {
  generate1099S: {
    batch: TaxFormBatch;
    message: string;
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function TaxFilingCenterPage() {
  const tenantId = useTenantId();
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState<number>(currentYear - 1);
  const [selectedContractors, setSelectedContractors] = useState<Set<string>>(
    new Set(),
  );
  const [activeTab, setActiveTab] = useState<string | null>('contractors');
  const [batches, setBatches] = useState<TaxFormBatch[]>([]);

  // Query contractors eligible for 1099-NEC
  const {
    data: contractorsData,
    loading: contractorsLoading,
    error: contractorsError,
    refetch: refetchContractors,
  } = useQuery<ContractorsFor1099Data>(GET_CONTRACTORS_FOR_1099, {
    variables: { tenantId, taxYear },
    skip: !tenantId,
  });

  // Generate 1099s mutation
  const [generate1099S, { loading: generating }] =
    useMutation<Generate1099SData>(GENERATE_1099S);

  const contractors = contractorsData?.contractorsFor1099 ?? [];

  const totalCompensation = useMemo(
    () =>
      contractors.reduce(
        (sum, c) => sum + parseFloat(c.totalCompensation || '0'),
        0,
      ),
    [contractors],
  );

  const toggleContractor = (id: string) => {
    setSelectedContractors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedContractors.size === contractors.length) {
      setSelectedContractors(new Set());
    } else {
      setSelectedContractors(new Set(contractors.map((c) => c.contractorId)));
    }
  };

  const handleGenerate = async () => {
    if (!tenantId) return;

    try {
      const contractorIds =
        selectedContractors.size > 0
          ? Array.from(selectedContractors)
          : undefined;

      const { data } = await generate1099S({
        variables: {
          input: {
            taxYear,
            generatePdfs: true,
            contractorIds,
          },
        },
      });

      if (data?.generate1099S?.batch) {
        setBatches((prev) => [data.generate1099S.batch, ...prev]);
        notifications.show({
          title: '1099-NEC Batch Created',
          message: data.generate1099S.message,
          color: 'green',
        });
        setActiveTab('batches');
      }
    } catch (err) {
      notifications.show({
        title: 'Generation Failed',
        message:
          err instanceof Error ? err.message : 'Failed to generate 1099-NEC forms',
        color: 'red',
      });
    }
  };

  // IRS deadlines (from wf-2-0-017 config)
  const recipientDeadline = new Date(taxYear + 1, 0, 31); // Jan 31
  const irsDeadline = new Date(taxYear + 1, 2, 31); // Mar 31
  const now = new Date();
  const daysToRecipientDeadline = Math.ceil(
    (recipientDeadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  const daysToIrsDeadline = Math.ceil(
    (irsDeadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  return (
    <Stack gap="md">
      <PageHeader
        title="Tax Filing Center"
        subtitle={`1099-NEC forms for tax year ${taxYear}`}
        breadcrumbs={[
          { label: 'Pay', to: '/admin/workforce/payroll' },
          { label: 'Tax Filing Center' },
        ]}
        actions={
          <Group gap="sm">
            <NumberInput
              value={taxYear}
              onChange={(val) => val && setTaxYear(Number(val))}
              min={2020}
              max={currentYear}
              w={100}
              size="sm"
            />
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={() => refetchContractors()}
              loading={contractorsLoading}
            >
              Refresh
            </Button>
          </Group>
        }
      />

      {/* Deadline Alerts */}
      {daysToRecipientDeadline > 0 && daysToRecipientDeadline <= 30 && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="orange"
          variant="light"
        >
          <Text size="sm">
            <Text span fw={600}>
              Recipient copies due in {daysToRecipientDeadline} days
            </Text>{' '}
            (Jan 31). Generate and distribute 1099-NEC Copy B now.
          </Text>
        </Alert>
      )}

      {/* Summary Stats */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Card withBorder p="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Contractors
              </Text>
              <Text size="xl" fw={700}>
                {contractorsLoading ? (
                  <Loader size="sm" />
                ) : (
                  contractors.length
                )}
              </Text>
            </div>
            <ThemeIcon size={48} radius="md" variant="light" color="blue">
              <IconUsers size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder p="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Total Compensation
              </Text>
              <Text size="xl" fw={700}>
                <CurrencyDisplay amount={totalCompensation} size="lg" />
              </Text>
            </div>
            <ThemeIcon size={48} radius="md" variant="light" color="green">
              <IconFileInvoice size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder p="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Batches Generated
              </Text>
              <Text size="xl" fw={700}>
                {batches.length}
              </Text>
            </div>
            <ThemeIcon size={48} radius="md" variant="light" color="orange">
              <IconSend size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder p="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                IRS Deadline
              </Text>
              <Text size="xl" fw={700}>
                {daysToIrsDeadline > 0 ? `${daysToIrsDeadline}d` : 'Passed'}
              </Text>
            </div>
            <ThemeIcon
              size={48}
              radius="md"
              variant="light"
              color={daysToIrsDeadline <= 14 ? 'red' : 'gray'}
            >
              <IconClock size={24} />
            </ThemeIcon>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab
            value="contractors"
            leftSection={<IconUsers size={14} />}
          >
            Contractors ({contractors.length})
          </Tabs.Tab>
          <Tabs.Tab
            value="batches"
            leftSection={<IconFileInvoice size={14} />}
          >
            Batches ({batches.length})
          </Tabs.Tab>
        </Tabs.List>

        {/* Contractors Tab */}
        <Tabs.Panel value="contractors" pt="md">
          <Stack gap="sm">
            {contractorsError && (
              <Alert color="red" variant="light">
                Failed to load contractors: {contractorsError.message}
              </Alert>
            )}

            {contractorsLoading && (
              <Group justify="center" py="xl">
                <Loader />
                <Text c="dimmed">
                  Querying contractor payments for {taxYear}...
                </Text>
              </Group>
            )}

            {!contractorsLoading && contractors.length === 0 && (
              <Alert
                color="blue"
                variant="light"
                icon={<IconCheck size={16} />}
              >
                No contractors meet the $600 threshold for tax year {taxYear}.
              </Alert>
            )}

            {contractors.length > 0 && (
              <>
                <Group justify="space-between">
                  <Button variant="subtle" size="sm" onClick={selectAll}>
                    {selectedContractors.size === contractors.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                  <Button
                    leftSection={<IconFileInvoice size={16} />}
                    onClick={handleGenerate}
                    loading={generating}
                    disabled={contractors.length === 0}
                  >
                    Generate 1099-NEC
                    {selectedContractors.size > 0
                      ? ` (${selectedContractors.size})`
                      : ` (All ${contractors.length})`}
                  </Button>
                </Group>

                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40} />
                      <Table.Th>Contractor</Table.Th>
                      <Table.Th>Payments</Table.Th>
                      <Table.Th ta="right">Compensation</Table.Th>
                      <Table.Th ta="right">Fed Withheld</Table.Th>
                      <Table.Th ta="right">State Withheld</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {contractors.map((contractor) => (
                      <Table.Tr
                        key={contractor.contractorId}
                        onClick={() =>
                          toggleContractor(contractor.contractorId)
                        }
                        style={{ cursor: 'pointer' }}
                        bg={
                          selectedContractors.has(contractor.contractorId)
                            ? 'var(--mantine-color-blue-light)'
                            : undefined
                        }
                      >
                        <Table.Td>
                          <input
                            type="checkbox"
                            checked={selectedContractors.has(
                              contractor.contractorId,
                            )}
                            onChange={() =>
                              toggleContractor(contractor.contractorId)
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text fw={500}>{contractor.contractorName}</Text>
                          <Text size="xs" c="dimmed">
                            {contractor.contractorId}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">
                            {contractor.paymentCount}
                          </Badge>
                        </Table.Td>
                        <Table.Td ta="right">
                          <CurrencyDisplay
                            amount={parseFloat(
                              contractor.totalCompensation || '0',
                            )}
                            size="sm"
                          />
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm">
                            {parseFloat(
                              contractor.federalTaxWithheld || '0',
                            ) > 0 ? (
                              <CurrencyDisplay
                                amount={parseFloat(
                                  contractor.federalTaxWithheld,
                                )}
                                size="sm"
                              />
                            ) : (
                              '-'
                            )}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm">
                            {parseFloat(
                              contractor.stateTaxWithheld || '0',
                            ) > 0 ? (
                              <CurrencyDisplay
                                amount={parseFloat(
                                  contractor.stateTaxWithheld,
                                )}
                                size="sm"
                              />
                            ) : (
                              '-'
                            )}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Batches Tab */}
        <Tabs.Panel value="batches" pt="md">
          <Stack gap="sm">
            {batches.length === 0 ? (
              <Alert
                color="blue"
                variant="light"
                icon={<IconFileInvoice size={16} />}
              >
                No batches generated yet. Select contractors and generate
                1099-NEC forms from the Contractors tab.
              </Alert>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Batch ID</Table.Th>
                    <Table.Th>Tax Year</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th ta="right">Forms</Table.Th>
                    <Table.Th ta="right">Errors</Table.Th>
                    <Table.Th>Created</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {batches.map((batch) => (
                    <Table.Tr key={batch.id}>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {batch.id}
                        </Text>
                      </Table.Td>
                      <Table.Td>{batch.taxYear}</Table.Td>
                      <Table.Td>
                        <Badge
                          color={
                            batch.status === 'COMPLETED'
                              ? 'green'
                              : batch.status === 'FAILED'
                                ? 'red'
                                : batch.status === 'PARTIAL_FAILURE'
                                  ? 'orange'
                                  : 'blue'
                          }
                          variant="light"
                          size="sm"
                        >
                          {batch.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right">
                        {batch.formsGenerated}/{batch.totalForms}
                      </Table.Td>
                      <Table.Td ta="right">
                        {batch.formsWithErrors > 0 ? (
                          <Text c="red" size="sm" fw={600}>
                            {batch.formsWithErrors}
                          </Text>
                        ) : (
                          '-'
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {new Date(batch.createdAt).toLocaleString()}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* IRS Deadline Reference */}
      <Paper withBorder p="sm" radius="md">
        <Group gap="lg">
          <ThemeIcon size={24} variant="light" color="gray">
            <IconCalendar size={14} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            IRS Deadlines for {taxYear}: Recipient copies by Jan 31 | Electronic
            filing by Mar 31 | Corrections by Mar 31
          </Text>
        </Group>
      </Paper>
    </Stack>
  );
}
